import { copy } from '@ohear/shared'
import { Badge, Button, Card, Spinner, useToast } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
/**
 * Account → Data export tab.
 *
 * Lets the signed-in actor request a zip of every take they've recorded
 * (across all orgs they participate in). The flow:
 *
 *   1. Click "Request data export" → POST /api/v1/me/data-export → job_id
 *   2. UI stashes the latest job_id in localStorage so a refresh resumes
 *      polling.
 *   3. Polls GET /api/v1/me/data-export/:job_id every 3s until status flips
 *      to `completed` or `failed`.
 *   4. On `completed`, surfaces the 24-hour presigned download link.
 *
 * Org owners get a separate row that exports the entire organisation
 * (projects, episodes, scenes, cues, characters, contracts, milestones,
 * comments, audit subset). Audio is NOT included — the bundle is data only,
 * to keep size reasonable. Owners can still pull audio via the actor
 * self-export and the producer "download stems" affordance.
 */
import { useEffect, useState } from 'react'
import { AccountTabs } from '../../features/account/AccountTabs'
import { useAccountProfile } from '../../lib/api/account'
import {
  type DataExportJobStatus,
  useMyDataExportStatus,
  useOrgDataExportStatus,
  useStartMyDataExport,
  useStartOrgDataExport,
} from '../../lib/api/data-export'
import { useAuth } from '../../lib/auth'
import { useT } from '../../lib/i18n/use-t'
import { AccountHeader } from './profile'

export const Route = createFileRoute('/account/data-export')({
  component: DataExportPage,
})

const LS_USER_JOB_KEY = 'ohear:data-export:user-job-id'
const LS_ORG_JOB_KEY = (orgId: string) => `ohear:data-export:org-job-id:${orgId}`

function DataExportPage() {
  const t = useT()
  const { user } = useAuth()
  const profile = useAccountProfile()
  const toast = useToast()

  const [userJobId, setUserJobId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.localStorage.getItem(LS_USER_JOB_KEY),
  )
  const start = useStartMyDataExport()
  const status = useMyDataExportStatus(userJobId)

  // Keep localStorage in sync.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (userJobId) window.localStorage.setItem(LS_USER_JOB_KEY, userJobId)
    else window.localStorage.removeItem(LS_USER_JOB_KEY)
  }, [userJobId])

  if (!user) {
    return (
      <Layout>
        <Card>
          <p className="m-0">{t(copy.common.SIGN_IN_REQUIRED.data_export)}</p>
        </Card>
      </Layout>
    )
  }

  if (profile.isLoading || !profile.data) {
    return (
      <Layout isActor={false}>
        <div className="flex items-center gap-2 text-[var(--text-2)]">
          <Spinner /> Loading account...
        </div>
      </Layout>
    )
  }

  const isActor = profile.data.primary_role === 'actor'
  const isOwner = profile.data.primary_role === 'owner'
  const orgId = (profile.data as unknown as { organization_id?: string }).organization_id

  const onStart = () => {
    start.mutate(undefined, {
      onSuccess: (job) => {
        setUserJobId(job.job_id)
        toast.show({
          severity: 'success',
          title: 'Export started',
          description: 'We will email you when the download is ready.',
        })
      },
      onError: (err) => {
        toast.show({
          severity: 'error',
          title: 'Could not start export',
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      },
    })
  }

  const onClear = () => {
    setUserJobId(null)
  }

  return (
    <Layout isActor={isActor} profile={profile.data} onSignOut={undefined}>
      <div className="flex flex-col gap-4 max-w-2xl">
        <Card title="Your takes (data export)">
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Download a zip with every recording you've uploaded, organised by project and character.
            Includes a <code>manifest.csv</code> with metadata (cue, take number, QC verdict) and an{' '}
            <code>index.html</code> for offline browsing.
          </p>
          <p className="text-[12px] text-[var(--text-2)] m-0 mt-2">
            Export links expire 24 hours after the job completes. You can request a fresh export at
            any time.
          </p>
          <div className="flex items-center gap-2 mt-4">
            <Button
              variant="primary"
              loading={start.isPending}
              onClick={onStart}
              disabled={status.data?.status === 'processing'}
            >
              Request data export
            </Button>
            {userJobId ? (
              <Button variant="ghost" onClick={onClear}>
                Forget last job
              </Button>
            ) : null}
          </div>

          {userJobId ? (
            <div className="mt-4 border-t border-[var(--border)] pt-4">
              <ExportStatusRow data={status.data} loading={status.isLoading} />
            </div>
          ) : null}
        </Card>

        {isOwner && orgId ? <OrgExportCard orgId={orgId} /> : null}
      </div>
    </Layout>
  )
}

function ExportStatusRow({
  data,
  loading,
}: {
  data?: DataExportJobStatus
  loading: boolean
}) {
  if (loading || !data) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
        <Spinner /> Checking job status...
      </div>
    )
  }
  const variant = data.status === 'completed' ? 'green' : data.status === 'failed' ? 'red' : 'amber'
  return (
    <div className="flex flex-col gap-2 text-[13px]">
      <div className="flex items-center gap-2">
        <Badge variant={variant}>{data.status}</Badge>
        <span className="text-[var(--text-2)]">Started {formatRelative(data.created_at)}</span>
        {data.size_bytes ? (
          <span className="text-[var(--text-2)] ml-auto">{formatBytes(data.size_bytes)}</span>
        ) : null}
      </div>

      {data.status === 'completed' && data.url ? (
        <a href={data.url} download className="text-[var(--accent)] underline text-[13px]">
          Download zip
          {data.expires_at ? (
            <span className="text-[var(--text-2)] ml-2">
              (expires {formatRelative(data.expires_at)})
            </span>
          ) : null}
        </a>
      ) : null}

      {data.status === 'failed' && data.error ? (
        <p className="text-[var(--danger)] text-[13px] m-0">{data.error}</p>
      ) : null}
    </div>
  )
}

function OrgExportCard({ orgId }: { orgId: string }) {
  const toast = useToast()
  const [orgJobId, setOrgJobId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.localStorage.getItem(LS_ORG_JOB_KEY(orgId)),
  )
  const start = useStartOrgDataExport(orgId)
  const status = useOrgDataExportStatus(orgId, orgJobId)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (orgJobId) window.localStorage.setItem(LS_ORG_JOB_KEY(orgId), orgJobId)
    else window.localStorage.removeItem(LS_ORG_JOB_KEY(orgId))
  }, [orgId, orgJobId])

  const onStart = () => {
    start.mutate(undefined, {
      onSuccess: (job) => {
        setOrgJobId(job.job_id)
        toast.show({ severity: 'success', title: 'Org export started' })
      },
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: 'Could not start org export',
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
    })
  }

  return (
    <Card title="Organisation export (owner)">
      <p className="text-[13px] text-[var(--text-2)] m-0">
        Bundle every project, episode, scene, cue, character, contract, milestone, comment, and the
        most recent 5000 audit events for this organisation. Data only — audio files live in storage
        and are excluded from this archive.
      </p>
      <div className="flex items-center gap-2 mt-4">
        <Button
          variant="primary"
          loading={start.isPending}
          onClick={onStart}
          disabled={status.data?.status === 'processing'}
        >
          Request org export
        </Button>
        {orgJobId ? (
          <Button variant="ghost" onClick={() => setOrgJobId(null)}>
            Forget last job
          </Button>
        ) : null}
      </div>
      {orgJobId ? (
        <div className="mt-4 border-t border-[var(--border)] pt-4">
          <ExportStatusRow data={status.data} loading={status.isLoading} />
        </div>
      ) : null}
    </Card>
  )
}

function Layout({
  children,
  isActor = false,
  profile,
  onSignOut,
}: {
  children: React.ReactNode
  isActor?: boolean
  profile?: Parameters<typeof AccountHeader>[0]['profile']
  onSignOut?: () => void
}) {
  // Conceptually a "security/privacy" surface — we keep the Security tab
  // highlighted since the AccountTabs strip is a closed enum. A "data export"
  // sub-link can be added inside Security in a follow-up.
  return (
    <section className="flex flex-col gap-4">
      <AccountHeader profile={profile} onSignOut={onSignOut} />
      <AccountTabs active="security" isActor={isActor}>
        {children}
      </AccountTabs>
    </section>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const abs = Math.abs(ms)
  const past = ms >= 0
  const minutes = Math.round(abs / 60_000)
  if (minutes < 1) return past ? 'just now' : 'in <1 min'
  if (minutes < 60) return past ? `${minutes}m ago` : `in ${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return past ? `${hours}h ago` : `in ${hours}h`
  const days = Math.round(hours / 24)
  return past ? `${days}d ago` : `in ${days}d`
}
